// /app/frontend/src/hooks/useOfflineSync.js
// React hook for offline sync functionality in measurement projects

import { useState, useEffect, useCallback } from 'react';
import { offlineManager } from '../utils/offlineManager';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useOfflineSync(token) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Initialize offline manager and subscribe to events
  useEffect(() => {
    const init = async () => {
      await offlineManager.init();
      await updatePendingCount();
    };
    init();

    const unsubscribe = offlineManager.subscribe(async (event) => {
      if (event.type === 'status') {
        setIsOnline(event.isOnline);
      }
      if (event.type === 'queue-updated' || event.type === 'sync-available') {
        await updatePendingCount();
      }
    });

    return unsubscribe;
  }, []);

  // Update pending sync count
  const updatePendingCount = useCallback(async () => {
    try {
      const pendingProjects = await offlineManager.getPendingProjects();
      const queueCount = await offlineManager.getSyncQueueCount();
      setPendingCount(pendingProjects.length + queueCount);
    } catch (error) {
      console.error('Error updating pending count:', error);
    }
  }, []);

  // Save project locally with sync status
  const saveProjectOffline = useCallback(async (project, isNew = false) => {
    const syncStatus = isNew ? 'new' : 'modified';
    await offlineManager.saveProjectLocally(project, syncStatus);
    await updatePendingCount();
    return project;
  }, [updatePendingCount]);

  // Create project (works offline)
  const createProjectOffline = useCallback(async (projectData) => {
    if (isOnline) {
      // Online: create via API and cache
      try {
        const response = await axios.post(`${API_URL}/api/measurement-projects`, projectData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await offlineManager.saveProjectLocally(response.data, 'synced');
        return { success: true, data: response.data, offline: false };
      } catch (error) {
        // If API fails, save offline
        console.warn('API failed, saving offline:', error);
      }
    }

    // Offline: create locally
    const tempProject = {
      id: offlineManager.generateTempId(),
      ...projectData,
      status: 'draft',
      windows: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _isOffline: true,
    };

    await offlineManager.saveProjectLocally(tempProject, 'new');
    await offlineManager.addToSyncQueue({
      type: 'CREATE_PROJECT',
      data: projectData,
      tempId: tempProject.id,
    });
    await updatePendingCount();

    return { success: true, data: tempProject, offline: true };
  }, [isOnline, token, updatePendingCount]);

  // Update project (works offline)
  const updateProjectOffline = useCallback(async (projectId, updates) => {
    if (isOnline && !projectId.startsWith('temp_')) {
      try {
        const response = await axios.put(
          `${API_URL}/api/measurement-projects/${projectId}`,
          updates,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await offlineManager.saveProjectLocally(response.data, 'synced');
        return { success: true, data: response.data, offline: false };
      } catch (error) {
        console.warn('API failed, saving offline:', error);
      }
    }

    // Offline: update locally
    const localProject = await offlineManager.getLocalProject(projectId);
    if (localProject) {
      const updatedProject = {
        ...localProject,
        ...updates,
        updated_at: new Date().toISOString(),
        _isOffline: true,
      };

      const syncStatus = localProject.syncStatus === 'new' ? 'new' : 'modified';
      await offlineManager.saveProjectLocally(updatedProject, syncStatus);

      // Only add to queue if not a temp project
      if (!projectId.startsWith('temp_')) {
        await offlineManager.addToSyncQueue({
          type: 'UPDATE_PROJECT',
          projectId,
          data: updates,
        });
      }
      await updatePendingCount();

      return { success: true, data: updatedProject, offline: true };
    }

    return { success: false, error: 'Project not found locally' };
  }, [isOnline, token, updatePendingCount]);

  // Add window to project (works offline)
  const addWindowOffline = useCallback(async (projectId, windowData) => {
    if (isOnline && !projectId.startsWith('temp_')) {
      try {
        const response = await axios.post(
          `${API_URL}/api/measurement-projects/${projectId}/windows`,
          windowData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local cache
        const localProject = await offlineManager.getLocalProject(projectId);
        if (localProject) {
          localProject.windows = [...(localProject.windows || []), response.data];
          await offlineManager.saveProjectLocally(localProject, 'synced');
        }
        
        return { success: true, data: response.data, offline: false };
      } catch (error) {
        console.warn('API failed, saving offline:', error);
      }
    }

    // Offline: add window locally
    const localProject = await offlineManager.getLocalProject(projectId);
    if (localProject) {
      const newWindow = {
        id: offlineManager.generateTempId(),
        ...windowData,
        main_photo_url: null,
        additional_photos: [],
        voice_notes: [],
        created_at: new Date().toISOString(),
        _isOffline: true,
      };

      localProject.windows = [...(localProject.windows || []), newWindow];
      localProject.updated_at = new Date().toISOString();

      const syncStatus = localProject.syncStatus === 'new' ? 'new' : 'modified';
      await offlineManager.saveProjectLocally(localProject, syncStatus);

      if (!projectId.startsWith('temp_')) {
        await offlineManager.addToSyncQueue({
          type: 'ADD_WINDOW',
          projectId,
          data: windowData,
          tempWindowId: newWindow.id,
        });
      }
      await updatePendingCount();

      return { success: true, data: newWindow, offline: true };
    }

    return { success: false, error: 'Project not found locally' };
  }, [isOnline, token, updatePendingCount]);

  // Update window (works offline)
  const updateWindowOffline = useCallback(async (projectId, windowId, updates) => {
    if (isOnline && !projectId.startsWith('temp_') && !windowId.startsWith('temp_')) {
      try {
        const response = await axios.put(
          `${API_URL}/api/measurement-projects/${projectId}/windows/${windowId}`,
          updates,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Update local cache
        const localProject = await offlineManager.getLocalProject(projectId);
        if (localProject) {
          localProject.windows = localProject.windows.map(w =>
            w.id === windowId ? { ...w, ...response.data } : w
          );
          await offlineManager.saveProjectLocally(localProject, 'synced');
        }

        return { success: true, data: response.data, offline: false };
      } catch (error) {
        console.warn('API failed, saving offline:', error);
      }
    }

    // Offline: update locally
    const localProject = await offlineManager.getLocalProject(projectId);
    if (localProject) {
      localProject.windows = (localProject.windows || []).map(w =>
        w.id === windowId ? { ...w, ...updates, _isOffline: true } : w
      );
      localProject.updated_at = new Date().toISOString();

      const syncStatus = localProject.syncStatus === 'new' ? 'new' : 'modified';
      await offlineManager.saveProjectLocally(localProject, syncStatus);

      if (!projectId.startsWith('temp_') && !windowId.startsWith('temp_')) {
        await offlineManager.addToSyncQueue({
          type: 'UPDATE_WINDOW',
          projectId,
          windowId,
          data: updates,
        });
      }
      await updatePendingCount();

      return { success: true, data: { ...updates }, offline: true };
    }

    return { success: false, error: 'Project not found locally' };
  }, [isOnline, token, updatePendingCount]);

  // Save photo for later upload (when offline)
  const savePhotoOffline = useCallback(async (projectId, windowId, file, photoType, tag = '') => {
    const base64 = await offlineManager.fileToBase64(file);
    
    const photoData = {
      projectId,
      windowId,
      photoType,
      tag,
      filename: file.name,
      contentType: file.type,
      base64Data: base64,
    };

    const savedPhoto = await offlineManager.savePendingPhoto(photoData);

    // Update local project with placeholder
    const localProject = await offlineManager.getLocalProject(projectId);
    if (localProject) {
      const windowIndex = localProject.windows.findIndex(w => w.id === windowId);
      if (windowIndex >= 0) {
        if (photoType === 'main') {
          localProject.windows[windowIndex].main_photo_url = base64;
          localProject.windows[windowIndex]._pendingMainPhoto = savedPhoto.id;
        } else {
          localProject.windows[windowIndex].additional_photos = [
            ...(localProject.windows[windowIndex].additional_photos || []),
            { id: savedPhoto.id, url: base64, tag, _isPending: true }
          ];
        }
        localProject.updated_at = new Date().toISOString();
        
        const syncStatus = localProject.syncStatus === 'new' ? 'new' : 'modified';
        await offlineManager.saveProjectLocally(localProject, syncStatus);
      }
    }

    await updatePendingCount();
    return savedPhoto;
  }, [updatePendingCount]);

  // Sync all pending changes
  const syncAll = useCallback(async () => {
    if (!isOnline || isSyncing) return { success: false, message: 'Offline or already syncing' };

    setIsSyncing(true);
    setSyncError(null);

    try {
      const syncQueue = await offlineManager.getSyncQueue();
      const pendingProjects = await offlineManager.getPendingProjects();
      
      let syncedCount = 0;
      let errorCount = 0;
      const tempIdMap = new Map(); // Map temp IDs to real IDs

      // First, sync new projects
      for (const project of pendingProjects.filter(p => p.syncStatus === 'new')) {
        try {
          const { _isOffline, syncStatus, lastModified, ...projectData } = project;
          
          // Remove temp ID for API call
          const { id: tempId, ...createData } = projectData;
          
          const response = await axios.post(`${API_URL}/api/measurement-projects`, {
            project_name: createData.project_name || 'Untitled Project',
            room_type: createData.room_type || 'Living Room',
            custom_room_name: createData.custom_room_name,
            customer_name: createData.customer_name,
            customer_address: createData.customer_address,
            customer_phone: createData.customer_phone,
            customer_email: createData.customer_email,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          tempIdMap.set(tempId, response.data.id);

          // Sync windows for this project
          for (const window of (createData.windows || [])) {
            const { id: tempWindowId, _isOffline: wOffline, ...windowData } = window;
            const windowResponse = await axios.post(
              `${API_URL}/api/measurement-projects/${response.data.id}/windows`,
              { window_name: windowData.window_name || 'Window 1' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Update window with details
            if (Object.keys(windowData).length > 1) {
              await axios.put(
                `${API_URL}/api/measurement-projects/${response.data.id}/windows/${windowResponse.data.id}`,
                windowData,
                { headers: { Authorization: `Bearer ${token}` } }
              );
            }
          }

          // Remove old local project and save synced version
          await offlineManager.deleteLocalProject(tempId);
          await offlineManager.saveProjectLocally(response.data, 'synced');
          syncedCount++;
        } catch (error) {
          console.error('Error syncing new project:', error);
          errorCount++;
        }
      }

      // Then, sync modified projects
      for (const project of pendingProjects.filter(p => p.syncStatus === 'modified')) {
        try {
          const { _isOffline, syncStatus, lastModified, ...projectData } = project;
          
          await axios.put(
            `${API_URL}/api/measurement-projects/${project.id}`,
            projectData,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          await offlineManager.saveProjectLocally(project, 'synced');
          syncedCount++;
        } catch (error) {
          console.error('Error syncing modified project:', error);
          errorCount++;
        }
      }

      // Process sync queue items
      for (const item of syncQueue) {
        try {
          const projectId = tempIdMap.get(item.projectId) || item.projectId;
          
          switch (item.type) {
            case 'UPDATE_PROJECT':
              await axios.put(
                `${API_URL}/api/measurement-projects/${projectId}`,
                item.data,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              break;
            case 'ADD_WINDOW':
              await axios.post(
                `${API_URL}/api/measurement-projects/${projectId}/windows`,
                item.data,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              break;
            case 'UPDATE_WINDOW':
              const windowId = item.windowId;
              await axios.put(
                `${API_URL}/api/measurement-projects/${projectId}/windows/${windowId}`,
                item.data,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              break;
          }

          await offlineManager.removeFromSyncQueue(item.id);
          syncedCount++;
        } catch (error) {
          console.error('Error processing sync queue item:', error);
          errorCount++;
        }
      }

      // Sync pending photos
      const pendingPhotos = await offlineManager.db.transaction(
        'pendingPhotos', 'readonly'
      ).objectStore('pendingPhotos').getAll();
      
      // Note: Photo syncing would require converting base64 back to file
      // This is a simplified version - full implementation would handle photos

      await updatePendingCount();
      setLastSyncTime(new Date().toISOString());

      return {
        success: errorCount === 0,
        synced: syncedCount,
        errors: errorCount,
        message: errorCount === 0 
          ? `Successfully synced ${syncedCount} items`
          : `Synced ${syncedCount} items with ${errorCount} errors`
      };
    } catch (error) {
      console.error('Sync error:', error);
      setSyncError(error.message);
      return { success: false, message: error.message };
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, token, updatePendingCount]);

  // Cache options for offline use
  const cacheOptionsData = useCallback(async (options) => {
    await offlineManager.cacheOptions('measurementOptions', options);
  }, []);

  // Get cached options
  const getCachedOptions = useCallback(async () => {
    return offlineManager.getCachedOptions('measurementOptions');
  }, []);

  // Fetch and merge projects (online + offline)
  const fetchMergedProjects = useCallback(async () => {
    const localProjects = await offlineManager.getLocalProjects();
    
    if (!isOnline) {
      return localProjects;
    }

    try {
      const response = await axios.get(`${API_URL}/api/measurement-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const serverProjects = response.data;
      
      // Cache server projects locally
      for (const project of serverProjects) {
        const localVersion = localProjects.find(p => p.id === project.id);
        if (!localVersion || localVersion.syncStatus === 'synced') {
          await offlineManager.saveProjectLocally(project, 'synced');
        }
      }

      // Merge: server projects + local-only projects (temp IDs)
      const localOnlyProjects = localProjects.filter(p => 
        p.id.startsWith('temp_') || p.syncStatus === 'new'
      );

      return [...serverProjects, ...localOnlyProjects];
    } catch (error) {
      console.error('Error fetching projects:', error);
      return localProjects;
    }
  }, [isOnline, token]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    syncError,
    // Operations
    saveProjectOffline,
    createProjectOffline,
    updateProjectOffline,
    addWindowOffline,
    updateWindowOffline,
    savePhotoOffline,
    syncAll,
    // Utilities
    cacheOptionsData,
    getCachedOptions,
    fetchMergedProjects,
    updatePendingCount,
  };
}

export default useOfflineSync;
